import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { filter, find, forEach, map, reduce, sortBy } from 'lodash';
import { CurrentSchoolYear } from '../../../shared/constants/current-school-year.constant';
import { Toggles } from '../../../shared/constants/toggles.constant';
import { DateHelpers } from '../../../shared/services/date-helpers/date-helpers.service';
import { ToggleService } from '../../../shared/services/toggle/toggle.service';
import { ISchool } from '../../../shared/typings/interfaces/school.interface';
import { IStudentAssessment } from '../../../shared/typings/interfaces/student-assessment.interface';
import { IStudentSupport } from '../../../shared/typings/interfaces/student-support.interface';
import { IAssessmentSummaryItem, ICurrentGrade, IStudent } from '../../../shared/typings/interfaces/student.interface';
import { StudentPanelService } from '../student-panel.service';
import AcademicPanelEMSConfig from './student-academic-panel-ems.config';

export interface IAssessmentSummaryMap {
  iReadyELA?: IAssessmentSummaryItem;
  iReadyMath?: IAssessmentSummaryItem;
  ReadyELA?: IAssessmentSummaryItem;
  ReadyMath?: IAssessmentSummaryItem;
  'State AssessmentELA'?: IAssessmentSummaryItem;
  'State AssessmentMath'?: IAssessmentSummaryItem;
}

export interface IFandPTableRow {
  adminNumber: string;
  date: string;
  instructional: string;
  relative: {
    data: string;
    style?: string;
  };
  expected: string;
}

export interface IIReadyTableRow {
  adminNumber: number;
  level: string;
  score: string;
  relative: string;
}

export interface IReadyTableRow {
  adminNumber: number;
  score: string;
}

export interface IStateExamsTableRow {
  year: string;
  elaPR: number;
  elaGrowth: string;
  mathPR: number;
  mathGrowth: string;
}

export interface ICoursesTableRow {
  courseCode: string;
  mp1: { value: string; style: string };
  mp2: { value: string; style: string };
  mp3: { value: string; style: string };
  mp4: { value: string; style: string };
  mp5: { value: string; style: string };
}

@Component({
  selector: 'student-academic-panel-ems',
  encapsulation: ViewEncapsulation.None,
  template: require('./student-academic-panel-ems.component.html'),
  styles: [require('./student-academic-panel-ems.scss')],
  // styles: [require('./student-academic-panel-ems.scss'), require('../student-panel.scss'), require('../../student-container/student-container.component.scss')]
})
export class StudentAcademicPanelEMS implements OnInit {
  @Input() student: IStudent;
  @Input() school: ISchool;
  @Input() collapsed: boolean;
  @Input() studentSupports: IStudentSupport[];
  @Input() studentAssessments: IStudentAssessment[];

  constructor(
    private studentPanel: StudentPanelService,
    private dateHelpers: DateHelpers,
    private toggleService: ToggleService,
  ) {}

  // options to pass to acad panel child component
  readyExamName = 'Ready';
  ireadyExamName = 'iReady';

  // locals
  panelTitle: string;
  latestScoresColumns: string[];
  latestScoresData;
  latestScoresDataSource;
  latestScoresStyles: string[];
  historicalTablesColumns: string[];
  currentCoursesColumns: string[];
  currentCoursesDataSource;
  currCoursesColStyles: string[];
  mpCount: number;
  hasCurrCourses: boolean;
  fullName: string;
  activeStudentSupports: IStudentSupport[];
  currentSy = CurrentSchoolYear.WITH_SY_PREFIX;

  // tiles
  showFandPTile: boolean;
  showiReadyTile: boolean;
  showReadyTile: boolean;
  stateElaScore: number | string;
  stateMathScore: number | string;
  fAndPScore: any;
  iReadyElaScore: number | string;
  iReadyMathScore: number | string;
  iReadyElaTooltip: string;
  iReadyMathTooltip: string;
  readyElaScore: number | string;
  readyMathScore: number | string;
  supportsElaCount: number | string;
  supportsMathCount: number | string;
  elaSupportsTooltip: string;
  mathSupportsTooltip: string;

  // segmentedControls
  controlOptions: string[];
  controlIndex: number = 0;
  subjectOptions: string[] = ['ELA', 'Math'];
  subjectIndex: number = 0;

  // tables
  fAndPTableData: IFandPTableRow[];
  iReadyELATableData: IIReadyTableRow[];
  readyELATableData: IReadyTableRow[];
  iReadyMathTableData: IIReadyTableRow[];
  readyMathTableData: IReadyTableRow[];
  showELAAssessments: boolean;
  showMathAssessments: boolean;
  showExamsTable: boolean;
  showCoursesTable: boolean;
  currentTerm: number;

  // exams tab
  examsTableData: IStateExamsTableRow[];

  // courses tab
  coursesTableData: ICoursesTableRow[];

  // TODO: remove this once the toggle is on for all users (JE)
  nyctToggle: boolean;

  // ignoring until nyct toggle is off and we can remove the old panel logic (JE)
  /* istanbul ignore next */
  ngOnInit() {
    this.nyctToggle = this.toggleService.getToggleState(Toggles.TOGGLE_NYCT_MVP);
    const {
      studentDetails: {
        name: { first, last },
      },
      isMS,
      assmnts: { summary },
    } = this.student;
    this.fullName = `${first} ${last}`;
    this.currentTerm = this.school.currentTermNumber;
    this.controlOptions = isMS ? ['Assessments', 'Exams', 'Courses'] : ['Assessments', 'Exams'];

    this.panelTitle = AcademicPanelEMSConfig.title;
    this.collapsed = this.studentPanel.getInitialPanelState('student-academic-panel');
    this.latestScoresColumns = ['name', 'stateExam', 'iReady', 'ready', 'supports'];
    this.historicalTablesColumns = ['name', 'score1', 'score2', 'score3', 'score4'];
    this.currentCoursesColumns = ['name'];

    this.activeStudentSupports = filter(this.studentSupports, studentSupport => {
      return studentSupport.status === 'ACTIVE';
    });

    // TODO: update this in v3.2 to bucket supports by course subject
    let supportNames = '';
    forEach(this.activeStudentSupports, studentSupport => {
      supportNames += `${studentSupport.support.name}\n`;
    });
    supportNames = supportNames.substring(0, supportNames.length - 1);

    // parse data for 'Latest scores' table (JE)
    const stateEla = find(summary, score => {
      return score.subj === 'ELA' && score.name === 'State Assessment';
    });
    const stateExamEla = stateEla ? stateEla.latestScore : `&mdash;`;

    const iReadyEla = find(summary, score => {
      return score.subj === 'ELA' && score.name === 'iReady';
    });
    const iReadyExamEla = iReadyEla ? iReadyEla.latestRelLvl : `&mdash;`;
    const iReadyLatestEla = iReadyEla ? iReadyEla.latestScore : `-`;
    const iReadyLatestLvlEla = iReadyEla ? iReadyEla.latestAbsLvl : `-`;

    const readyEla = find(summary, score => {
      return score.subj === 'ELA' && score.name === 'Ready';
    });
    const readyExamEla = readyEla ? readyEla.latestScore : null;

    const stateMath = find(summary, score => {
      return score.subj === 'Math' && score.name === 'State Assessment';
    });
    const stateExamMath = stateMath ? stateMath.latestScore : `&mdash;`;

    const iReadyMath = find(summary, score => {
      return score.subj === 'Math' && score.name === 'iReady';
    });
    const iReadyExamMath = iReadyMath ? iReadyMath.latestRelLvl : `&mdash;`;
    const iReadyLatestMath = iReadyMath ? iReadyMath.latestScore : `-`;
    const iReadyLatestLvlMath = iReadyMath ? iReadyMath.latestAbsLvl : `-`;

    const readyMath = find(summary, score => {
      return score.subj === 'Math' && score.name === 'Ready';
    });
    const readyExamMath = readyMath ? readyMath.latestScore : null;

    // structure latest scores data for template (JE)
    this.latestScoresData = [
      {
        rowName: 'ELA',
        stateExam: stateExamEla,
        iReady: iReadyExamEla,
        iReadyTooltip: iReadyLatestEla ? `Score: ${iReadyLatestEla} Placement: ${iReadyLatestLvlEla}` : null,
        ready: this.formatPercent(readyExamEla),
        supports: this.activeStudentSupports.length,
        supportsTooltip: supportNames,
      },
      {
        rowName: 'Math',
        stateExam: stateExamMath,
        iReady: iReadyExamMath,
        iReadyTooltip: iReadyLatestMath ? `Score: ${iReadyLatestMath} Placement: ${iReadyLatestLvlMath}` : null,
        ready: this.formatPercent(readyExamMath),
        supports: this.activeStudentSupports.length,
        supportsTooltip: supportNames,
      },
    ];

    this.latestScoresDataSource = new MatTableDataSource(this.latestScoresData);

    // structure data for Current Courses Table
    let currCourses: any = {};
    this.student.currProgram.grades.forEach((course: ICurrentGrade) => {
      const { code, name, mp, subj, str, pf } = course;
      let style = pf === 'F' ? 'failing' : '';
      let humanSubj = this.parseHumanSubj(subj);
      let tooltip = 'Code: ' + code + ' Subject: ' + humanSubj;
      if (!currCourses[code] && mp !== 10) {
        // mp10 scores are not included in the table (JE)
        currCourses[code] = { subj: humanSubj, code, tooltip, name, scores: [{ mp, str, pf, style }] };
      } else if (mp !== 10) {
        let temp = currCourses[code];
        temp.scores = sortBy([...temp.scores, { mp, str, pf, style }], 'mp');
        currCourses[code] = temp;
      }
    });
    currCourses = sortBy(currCourses, 'subj');

    // Add marking period columns based on school.totalMps
    // TODO: remove check for totalMps after data migration (JE)
    this.mpCount = this.school.totalMps ? this.school.totalMps : 5;

    this.hasCurrCourses = this.student.currProgram && this.student.currProgram.grades.length > 0;

    let currCoursesData;
    let courseStyles = [];
    if (this.hasCurrCourses) {
      currCoursesData = currCourses.map(course => {
        let scores = [];
        for (let i = 0; i < this.mpCount; i++) {
          const mpScore = course.scores[i];
          if (mpScore && mpScore.mp) {
            scores.push(mpScore);
          } else {
            scores.push({ mp: i + 1, str: `&mdash;`, pf: null, style: 'no-data' });
          }
        }
        course.scores = scores;
        return course;
      });

      // TODO: remove this logic once MP data is on the school (JE)
      courseStyles = [];
      for (let i = 0; i < this.mpCount; i++) {
        this.currentCoursesColumns.push(`mp${i + 1}`);
        const course = currCoursesData[0];
        const scores = course.scores;
        if (scores[i].pf) courseStyles.push('');
        else courseStyles.push('no-data');
      }
    }

    this.currCoursesColStyles = courseStyles;

    this.currentCoursesDataSource = new MatTableDataSource(currCoursesData);

    this.setTiles();
    this.fAndPTableData = this._setFandPTable();
    this.iReadyELATableData = this._setIReadyTable(this.student, 'ELA');
    this.iReadyMathTableData = this._setIReadyTable(this.student, 'Math');
    this.readyELATableData = this._setReadyTable(this.student, 'ELA');
    this.readyMathTableData = this._setReadyTable(this.student, 'Math');
    this.showELAAssessments = !!(
      this.fAndPTableData.length ||
      this.iReadyELATableData.length ||
      this.readyELATableData.length
    );
    this.showMathAssessments = !!this.iReadyMathTableData.length || !!this.readyMathTableData.length;

    this.examsTableData = this._setExamsTable(this.student);
    this.showExamsTable = !!this.examsTableData.length;

    this.coursesTableData = this._setCoursesTable(this.student);
    this.showCoursesTable = !!this.coursesTableData.length;
  }

  formatPercent(percent: number): string {
    if (!percent) return `&mdash;`;
    return `${Math.round(percent * 100)}%`;
  }

  getStyleForScore(score): string {
    return score === `&mdash;` ? 'no-score' : null;
  }

  parseIReadyTooltip(admin) {
    if (!admin) return `&mdash;`;
    return admin.score;
  }

  parseHumanSubj(subj: string) {
    switch (subj) {
      case 'ELA':
        return 'ELA';
      case 'Math':
        return 'Math';
      case 'SS':
        return 'Social Sciences';
      case 'Sci':
        return 'Science';
      default:
        return subj;
    }
  }

  togglePanelBody(): void {
    this.collapsed = this.studentPanel.togglePanelBody(this.collapsed, this.panelTitle);
  }

  setTiles(): void {
    this.showFandPTile = !!find(
      this.studentAssessments,
      asmnt => asmnt.schoolAssessment.assessmentNickName === 'F & P',
    );
    const asmntsMap: IAssessmentSummaryMap = reduce(
      this.student.assmnts.summary,
      (result, asmnt) => {
        const key = `${asmnt.name}${asmnt.subj}`;
        result[key] = asmnt;
        return result;
      },
      {},
    );
    this.showiReadyTile = !!asmntsMap.iReadyELA || !!asmntsMap.iReadyMath;
    this.showReadyTile = !!asmntsMap.ReadyELA || !!asmntsMap.ReadyMath;

    // state assessments
    this._setStateAssessmentsTile(asmntsMap);
    // f & p
    this._setFandPTile();
    // iReady
    this._setiReadyTile(asmntsMap);
    // Ready
    this._setReadyTile(asmntsMap);
    // supports
    this._setSupportsTile();
  }

  _setStateAssessmentsTile(asmntsMap: IAssessmentSummaryMap): void {
    this.stateElaScore = asmntsMap['State AssessmentELA'] ? asmntsMap['State AssessmentELA'].latestScore : '—';
    this.stateMathScore = asmntsMap['State AssessmentMath'] ? asmntsMap['State AssessmentMath'].latestScore : '—';
  }

  _setFandPTile() {
    const fAndP: IStudentAssessment[] = filter(this.studentAssessments, asmnt => {
      const isFandP = asmnt.schoolAssessment.assessmentNickName === 'F & P';
      const isComplete = asmnt.status === 'COMPLETED';
      return isFandP && isComplete;
    });
    const sortedFandP = sortBy(fAndP, asmnt => asmnt.schoolAssessment.adminNumber);
    const relLevel = sortedFandP.length ? sortedFandP[sortedFandP.length - 1].relativeScore : null;
    const parsedRelLevel = parseInt(relLevel);
    const fpData = {
      data: '—',
      style: null,
    };

    if (parsedRelLevel <= -1) fpData.style = 'warning';

    if (parsedRelLevel >= 0) fpData.data = 'at or above';
    else if (parsedRelLevel === -1) fpData.data = '1 below';
    else if (parsedRelLevel === -2) fpData.data = '2 below';
    else if (parsedRelLevel <= -3) fpData.data = '3+ below';
    else if (!parsedRelLevel) fpData.data = '—';

    this.fAndPScore = fpData;
  }

  _setiReadyTile(asmntsMap: IAssessmentSummaryMap): void {
    this.iReadyElaScore = asmntsMap.iReadyELA ? asmntsMap.iReadyELA.latestRelLvl : '—';
    const elaScore = asmntsMap.iReadyELA ? asmntsMap.iReadyELA.latestScore : '—';
    const elaPlacement = asmntsMap.iReadyELA ? asmntsMap.iReadyELA.latestAbsLvl : '—';
    this.iReadyElaTooltip = `Score: ${elaScore} Placement: ${elaPlacement}`;
    this.iReadyMathScore = asmntsMap.iReadyMath ? asmntsMap.iReadyMath.latestRelLvl : '—';
    const mathScore = asmntsMap.iReadyMath ? asmntsMap.iReadyMath.latestScore : '—';
    const mathPlacement = asmntsMap.iReadyMath ? asmntsMap.iReadyMath.latestAbsLvl : '—';
    this.iReadyMathTooltip = `Score: ${mathScore} Placement: ${mathPlacement}`;
  }

  _setReadyTile(asmntsMap: IAssessmentSummaryMap): void {
    this.readyElaScore = asmntsMap.ReadyELA ? asmntsMap.ReadyELA.latestScore : '—';
    this.readyMathScore = asmntsMap.ReadyMath ? asmntsMap.ReadyMath.latestScore : '—';
  }

  _setSupportsTile(): void {
    const elaSupports = filter(
      this.activeStudentSupports,
      studSupport => studSupport.support.metaData && studSupport.support.metaData.subject === 'ELA',
    );
    const mathSupports = filter(
      this.activeStudentSupports,
      studSupport => studSupport.support.metaData && studSupport.support.metaData.subject === 'Math',
    );
    this.supportsElaCount = elaSupports.length ? elaSupports.length : '—';
    this.supportsMathCount = mathSupports.length ? mathSupports.length : '—';
    this.elaSupportsTooltip = reduce(
      elaSupports,
      (tooltip, studSupport) => {
        tooltip += `${studSupport.support.name}\n`;
        return tooltip;
      },
      '',
    );
    this.mathSupportsTooltip = reduce(
      mathSupports,
      (tooltip, studSupport) => {
        tooltip += `${studSupport.support.name}\n`;
        return tooltip;
      },
      '',
    );
  }

  changeTableControl(event) {
    this.controlIndex = event;
  }

  changeSubjectControl(event) {
    this.subjectIndex = event;
  }

  _setFandPTable(): IFandPTableRow[] {
    const rows = map(this.studentAssessments, studAsmnt => {
      const {
        schoolAssessment: { adminNumber },
        items,
        assessedOn,
        relativeScore,
        expectedScore,
      } = studAsmnt;
      let relative = {
        data: null,
        style: null,
      };

      const parsedRelative = relativeScore || relativeScore === 0 ? relativeScore : '';

      if (parsedRelative && parsedRelative <= -1) relative.style = 'warning';
      // previously checked to see if relative was null to assign '-'.
      // But null is >= 0, which trigger the 'At or above level' assignment.
      if (parsedRelative === '') relative.data = '—';
      else if (parsedRelative >= 0) relative.data = 'At or above level';
      else if (parsedRelative === -1) relative.data = '1 below';
      else if (parsedRelative === -2) relative.data = '2 below';
      else if (parsedRelative <= -3) relative.data = '3+ below';

      return {
        adminNumber: adminNumber ? adminNumber.toString() : '—',
        date: assessedOn ? this.dateHelpers.getFormattedMoment(assessedOn, 'MMM DD, YYYY') : '—',
        instructional: items[1].value ? items[1].value : '—',
        expected: expectedScore ? expectedScore : '—',
        relative,
      };
    });
    return sortBy(rows, row => row.adminNumber);
  }

  /* istanbul ignore next */
  _setIReadyTable(student: IStudent, subject: 'ELA' | 'Math'): IIReadyTableRow[] {
    const {
      assmnts: { history },
    } = student;
    const asmntsBySubj = filter(history, asmnt => {
      return (
        asmnt.name === 'iReady' && asmnt.subj === subject && asmnt.year === this.currentSy && asmnt.skill === 'Overall'
      );
    });
    const sortedAsmnts = sortBy(asmntsBySubj, asmnt => asmnt.admin);
    return map(sortedAsmnts, asmnt => {
      const { admin, absLvl, relLvl, score } = asmnt;
      return {
        adminNumber: admin,
        score: score ? score.toString() : '—',
        level: absLvl ? absLvl : '—',
        relative: relLvl ? relLvl : '—',
      };
    });
  }

  /* istanbul ignore next */
  _setReadyTable(student: IStudent, subject: 'ELA' | 'Math'): IReadyTableRow[] {
    const {
      assmnts: { history },
    } = student;
    const asmntsBySubj = filter(history, asmnt => {
      return (
        asmnt.name === 'Ready' && asmnt.subj === subject && asmnt.year === this.currentSy && asmnt.skill === 'Overall'
      );
    });
    const sortedAsmnts = sortBy(asmntsBySubj, asmnt => asmnt.admin);
    return map(sortedAsmnts, asmnt => {
      const { admin, score } = asmnt;
      return {
        adminNumber: admin,
        score: score ? score.toString() : '—',
      };
    });
  }

  /* istanbul ignore next */
  _setExamsTable(student: IStudent): IStateExamsTableRow[] {
    const {
      assmnts: { history },
    } = student;
    const stateExams = filter(history, item => item.name === 'State Assessment');
    const yearMap = reduce(
      stateExams,
      (result, item) => {
        const { year, subj, pr, growth } = item;
        const percent = Math.round(growth * 100);
        let charge;
        if (growth < 0) charge = '-';
        else if (growth > 0) charge = '+';
        else charge = '';
        const parsedGrowth = `${charge}${percent}%`;
        if (!result[year]) result[year] = { year, elaPR: '—', elaGrowth: '—', mathPR: '—', mathGrowth: '—' };
        if (subj === 'ELA') {
          result[year].elaPR = pr;
          result[year].elaGrowth = parsedGrowth;
        } else if (subj === 'Math') {
          result[year].mathPR = pr;
          result[year].mathGrowth = parsedGrowth;
        }
        return result;
      },
      {},
    );
    const years = Object.keys(yearMap);
    return map(years, year => yearMap[year]);
  }

  /* istanbul ignore next */
  _setCoursesTable(student: IStudent): ICoursesTableRow[] {
    const {
      currProgram: { grades },
    } = student;
    const courseGradesMap = reduce(
      grades,
      (result, grade) => {
        const { code, str, mp, num, pf } = grade;
        let value;
        if (pf) value = pf;
        else if (!str) value = '—';
        else value = str;

        let style;
        if (mp && mp !== 10) {
          if (pf === 'F') style = 'failing';
          else if (num >= 35 && num <= 55) style = 'failing-med';
          else if (num < 35) style = 'failing';
          else if (!num) style = 'missing';
          if (!result[code]) result[code] = { courseCode: code };
          result[code][`mp${mp}`] = { value, style };
          return result;
        } else {
          return result;
        }
      },
      {},
    );
    const courseCodes = Object.keys(courseGradesMap);
    const courses = map(courseCodes, code => courseGradesMap[code]);
    return sortBy(courses, course => course.courseCode);
  }
}
